import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Loader2, Upload, FileCheck2, Trash2, Sparkles, Download } from 'lucide-react';
import { toast } from 'sonner';
import { uploadResume, parseResume, deleteResume, downloadResumeSignedUrl } from '../lib/api';
import { ParseConfirmDialog } from './ParseConfirmDialog';

const ACCEPT = '.pdf,.docx,.jpg,.jpeg,.png,.heic,.heif';
const MAX_SIZE_MB = 10;

/**
 * Reusable upload + parse card. Used in:
 *   - AddSkillsScreen (signup, with autoParseAfterUpload=true, confirmBeforeApply=false)
 *   - Profile page (next to Certifications, autoParseAfterUpload=false, confirmBeforeApply=true)
 *
 * Props:
 *   existingFileUrl       string|null   path of any already-uploaded resume
 *   currentProfile        object|null   current profile values (used for diff in confirm dialog)
 *   onParsed              fn            called with extracted fields when user confirms (or auto-fill)
 *   onDeleted             fn            called when resume removed
 *   autoParseAfterUpload  boolean       if true, parse fires immediately after upload
 *   confirmBeforeApply    boolean       if true, show ParseConfirmDialog before calling onParsed
 */
export function ResumeUploadCard({
  existingFileUrl,
  currentProfile,
  onParsed,
  onDeleted,
  // Fires immediately after a successful upload with { file_url, uploaded_at }.
  // Parent should sync these into its profile state so the rest of the app
  // (dashboard "upload resume" banner, this card on revisit-without-reload)
  // sees that a resume now exists. The upload endpoint writes them to the DB,
  // but the in-memory `profile` object isn't otherwise refreshed.
  onUploaded,
  autoParseAfterUpload = false,
  confirmBeforeApply = false,
  // When true, the post-upload UI is collapsed to a single row (filename + Replace),
  // matching AnonymousResumeUploadCard on email signup. Used by ProfileCompletion
  // so the OAuth-signup experience matches manual-email-signup exactly.
  signupMode = false,
}) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [hasFile, setHasFile] = useState(Boolean(existingFileUrl));
  const [uploadedFilename, setUploadedFilename] = useState(null); // captured on upload — used in signupMode UI
  const [pendingExtracted, setPendingExtracted] = useState(null); // holds parse result while confirm dialog is open

  // Refs so the visible Buttons can trigger the hidden file inputs programmatically.
  // Wrapping <Button> inside a <label> doesn't work — Buttons swallow the click and the
  // label's associated <input> never sees the synthetic event.
  const initialUploadRef = useRef(null);
  const replaceUploadRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.info(t('resume.tooLarge', `File too large. Max ${MAX_SIZE_MB} MB.`));
      return;
    }

    setUploading(true);
    try {
      const result = await uploadResume(file);
      setUploadedFilename(file.name);
      setHasFile(true);
      toast.success(t('resume.uploaded', 'Resume uploaded'));
      // Sync the new resume_file_url + uploaded_at into the parent's profile state.
      // Without this the dashboard's "upload your resume" banner stays visible
      // until the next full page reload, and re-rendering this card from a stale
      // parent profile would show "Upload your resume" again.
      if (typeof onUploaded === 'function' && result) {
        try { await onUploaded(result); } catch (_) { /* parent handler errors shouldn't break upload */ }
      }
      if (autoParseAfterUpload) {
        await handleParse();
      }
    } catch (err) {
      toast.info(t('resume.uploadFailed', "We couldn't upload that file. Try a different one."));
      console.error('Resume upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleParse = async () => {
    setParsing(true);
    try {
      const extracted = await parseResume();
      // Detect Flow F (everything null) per spec
      const filledFields = Object.entries(extracted)
        .filter(([k]) => !k.startsWith('_'))
        .filter(([, v]) => v !== null && v !== '' && !(Array.isArray(v) && v.length === 0));
      if (filledFields.length === 0) {
        toast.info(t('resume.couldNotRead', "We couldn't read this resume. You can try a different file or fill in your profile below."));
        return;
      }

      if (confirmBeforeApply) {
        // Show diff modal — onParsed will fire when user clicks Apply with the SELECTED subset
        setPendingExtracted(extracted);
      } else {
        // Silent fill (signup flow)
        if (typeof onParsed === 'function') {
          await onParsed(extracted);
        }
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.info(t('resume.rateLimited', "You've used your 3 daily resume parses. Try again tomorrow."));
      } else if (status === 422 || status === 404) {
        toast.info(t('resume.couldNotRead', "We couldn't read this resume. You can try a different file or fill in your profile below."));
      } else {
        toast.info(t('resume.parseFailed', "We couldn't process the resume right now. Try again in a moment."));
      }
      console.error('Resume parse failed:', err);
    } finally {
      setParsing(false);
    }
  };

  const handleConfirmApply = async (selectedUpdates) => {
    // selectedUpdates is the subset of fields the user ticked
    const parsed = pendingExtracted;
    setPendingExtracted(null);
    if (!parsed || Object.keys(selectedUpdates).length === 0) return;
    // Build a synthetic "extracted" object containing only the selected fields,
    // preserving the original confidence map so flagging works correctly.
    const filtered = { ...selectedUpdates, _field_confidence: parsed._field_confidence || {} };
    if (typeof onParsed === 'function') {
      await onParsed(filtered);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteResume();
      setHasFile(false);
      if (typeof onDeleted === 'function') onDeleted();
      toast.success(t('resume.deleted', 'Resume removed'));
    } catch (err) {
      toast.info(t('resume.deleteFailed', "Couldn't remove resume."));
      console.error('Resume delete failed:', err);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const { signed_url } = await downloadResumeSignedUrl();
      // Open in new tab so the user can download/view; signed URL expires in 15 min
      window.open(signed_url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.info(t('resume.downloadFailed', "Couldn't get a download link. Try again in a moment."));
      console.error('Resume download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  const busy = uploading || parsing;

  return (
    <>
      <Card className="border-2 border-dashed border-primary/30 bg-primary/5">
        <CardContent className="p-6">
          {!hasFile ? (
            <div className="flex flex-col items-center justify-center text-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Upload className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="font-semibold">
                  {t('resume.uploadTitle', 'Upload your resume')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('resume.uploadSubtitle', "We'll fill out your whole profile from it")}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('resume.acceptFormats', 'PDF, DOCX, JPG, PNG, HEIC up to 10 MB')}
                </p>
              </div>
              <input
                ref={initialUploadRef}
                type="file"
                accept={ACCEPT}
                onChange={handleFileChange}
                className="hidden"
                disabled={busy}
              />
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                onClick={() => initialUploadRef.current?.click()}
              >
                {uploading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('resume.uploading', 'Uploading...')}</>
                ) : parsing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('resume.parsing', 'Reading your resume...')}</>
                ) : (
                  t('resume.chooseFile', 'Choose file')
                )}
              </Button>
            </div>
          ) : signupMode ? (
            // Signup-flow post-upload UI — matches AnonymousResumeUploadCard.
            // No auto-fill button (already auto-parsed), no delete/download (file
            // just got attached during signup; user finishes profile, then manages
            // the resume from the profile page later).
            <div className="flex items-center gap-3">
              <FileCheck2 className="w-5 h-5 text-success flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">
                  {uploadedFilename || t('resume.onFile', 'Resume on file')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('resume.signupParsedHint', "Form pre-filled below. You can re-pick to try a different resume.")}
                </p>
              </div>
              <input
                ref={replaceUploadRef}
                type="file"
                accept={ACCEPT}
                onChange={handleFileChange}
                className="hidden"
                disabled={busy}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => replaceUploadRef.current?.click()}
              >
                {(uploading || parsing) ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('resume.parsing', 'Reading your resume...')}</>
                ) : (
                  t('resume.replace', 'Replace')
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-success">
                <FileCheck2 className="w-5 h-5" />
                <span className="font-medium">{t('resume.onFile', 'Resume on file')}</span>
              </div>

              {/* Helper text explaining what Auto-fill does */}
              <p className="text-sm text-muted-foreground">
                {t(
                  'resume.autoFillHelp',
                  "Read your resume and use it to fill any missing profile fields, or update existing ones. You'll see a preview and choose which changes to apply."
                )}
              </p>

              <div className="flex flex-wrap gap-2">
                <Button onClick={handleParse} disabled={busy} size="sm">
                  {parsing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('resume.parsing', 'Reading your resume...')}</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" /> {t('resume.autoFill', 'Auto-fill from resume')}</>
                  )}
                </Button>
                <Button onClick={handleDownload} variant="outline" size="sm" disabled={busy || downloading}>
                  {downloading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('resume.downloading', 'Opening...')}</>
                  ) : (
                    <><Download className="w-4 h-4 mr-2" /> {t('resume.download', 'Download')}</>
                  )}
                </Button>
                <input
                  ref={replaceUploadRef}
                  type="file"
                  accept={ACCEPT}
                  onChange={handleFileChange}
                  className="hidden"
                  disabled={busy}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => replaceUploadRef.current?.click()}
                >
                  {t('resume.replace', 'Replace')}
                </Button>
                <Button onClick={handleDelete} variant="ghost" size="sm" disabled={busy}>
                  <Trash2 className="w-4 h-4 mr-2" /> {t('resume.delete', 'Delete')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirmation diff modal — only used when confirmBeforeApply is true */}
      <ParseConfirmDialog
        open={!!pendingExtracted}
        parsed={pendingExtracted}
        currentProfile={currentProfile}
        onApply={handleConfirmApply}
        onClose={() => setPendingExtracted(null)}
      />
    </>
  );
}

export default ResumeUploadCard;
